import React, { useState } from 'react';
import { BedDoubleIcon, PlusIcon, Trash2Icon, XIcon } from 'lucide-react';
import type { ChoirEvent, Member, RoomMode } from '../../types';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, inputClass } from '../ui/Field';
import { useApp } from '../../contexts/AppContext';
import { fullName, voiceLabel } from '../../utils/labels';
interface RoomBoardProps {
  event: ChoirEvent;
  udelezenci: Member[];
}
export function RoomBoard({
  event,
  udelezenci
}: RoomBoardProps) {
  const {
    rooms,
    assignRoom,
    addRoom,
    removeRoom,
    saveEvent,
    pushToast
  } = useApp();
  const [ime, setIme] = useState('');
  const [kapaciteta, setKapaciteta] = useState(2);
  const [dodajanje, setDodajanje] = useState(false);
  const nastanitev = event.potovanje?.nastanitev;
  if (!nastanitev) return null;
  const dodeljeno = rooms[event.id] ?? {};
  const brezSobe = udelezenci.filter(m => !dodeljeno[m.id]);
  const skupnaKapaciteta = nastanitev.sobe.reduce((s, r) => s + r.kapaciteta, 0);
  function nastaviNacin(nacin: RoomMode) {
    saveEvent({
      ...event,
      potovanje: {
        ...event.potovanje!,
        nastanitev: {
          ...nastanitev!,
          nacin
        }
      }
    });
    pushToast(nacin === 'vodstvo' ? 'Sobe razporeja vodstvo.' : 'Člani lahko izberejo sobo sami.');
  }
  return <div className="space-y-4" data-id="element-109" data-mp="3l1nix5hr4vwb">
      <Card className="p-5" data-id="element-110" data-mp="3g2u5fd2gt4w5">
        <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-111" data-mp="3g33u8xbrx609">
          <div data-id="element-112" data-mp="3bcdrlruqfh99">
            <div className="flex items-center gap-2" data-id="element-113" data-mp="3bbu7q5liuisl">
              <BedDoubleIcon className="h-4 w-4 text-green-600" data-id="element-114" data-mp="3ba0zwmz69sj0" />
              <h2 className="text-base font-semibold text-ink" data-id="element-115" data-mp="36dsp1ls0clhn">Nastanitev</h2>
            </div>
            <p className="mt-0.5 text-sm text-ink-muted" data-id="element-116" data-mp="31ezdw00716kn">{nastanitev.ime}</p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-117" data-mp="2xbb192wpfksj">
            <div className="flex gap-1.5" data-id="element-118" data-mp="2xe5nmzza3fol">
              {([['vodstvo', 'Razporeja vodstvo'], ['clani', 'Člani izberejo sobo']] as [RoomMode, string][]).map(([k, label]) => <button key={k} onClick={() => nastaviNacin(k)} aria-pressed={nastanitev.nacin === k} className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${nastanitev.nacin === k ? 'border-green-500 bg-green-500 text-white' : 'border-line bg-white text-ink-muted hover:bg-canvas'}`} data-id="element-119" data-mp="2ms0osbot6o93">
                  {label}
                </button>)}
            </div>
            <Button variant="secondary" size="sm" onClick={() => setDodajanje(v => !v)} data-id="element-120" data-mp="2wo3x5za97j4h">
              <PlusIcon className="h-4 w-4" data-id="element-121" data-mp="3kdy6w7za0rki" />
              Dodaj sobo
            </Button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-4" data-id="element-122" data-mp="3p7ao3x1mlw38">
          {[{
          label: 'Sob',
          value: nastanitev.sobe.length
        }, {
          label: 'Ležišč',
          value: skupnaKapaciteta
        }, {
          label: 'Razporejenih',
          value: udelezenci.length - brezSobe.length
        }, {
          label: 'Brez sobe',
          value: brezSobe.length
        }].map(s => <div key={s.label} className="rounded-lg bg-canvas px-3 py-2.5" data-id="element-123" data-mp="1ih796pijjrzd">
              <p className={`text-xl font-semibold ${s.label === 'Brez sobe' && s.value > 0 ? 'text-amber-600' : 'text-ink'}`} data-id="element-124" data-mp="uruz7nfe3fw0">
                {s.value}
              </p>
              <p className="text-[12px] text-ink-muted" data-id="element-125" data-mp="13tm9z6osecwq">{s.label}</p>
            </div>)}
        </div>

        {dodajanje && <div className="mt-4 grid gap-3 rounded-lg border border-line bg-canvas/60 p-3 sm:grid-cols-[1.5fr_1fr_auto] sm:items-end" data-id="element-126" data-mp="yvklu7ci7nee">
            <Field label="Ime sobe" htmlFor="sobaIme" data-id="element-127" data-mp="2a94zu2d04x82">
              <input id="sobaIme" value={ime} onChange={e => setIme(e.target.value)} placeholder="Npr. Soba 206 ali Apartma 1" className={inputClass} data-id="element-128" data-mp="2aaptlprhsc4v" />
            </Field>
            <Field label="Kapaciteta" htmlFor="sobaKap" data-id="element-129" data-mp="3ffy6b669a2h9">
              <input id="sobaKap" type="number" min={1} value={kapaciteta} onChange={e => setKapaciteta(Number(e.target.value))} className={inputClass} data-id="element-130" data-mp="3kg2i4iw7y915" />
            </Field>
            <Button onClick={() => {
          if (!ime.trim()) return;
          addRoom(event.id, {
            ime: ime.trim(),
            kapaciteta
          });
          setIme('');
          setKapaciteta(2);
          setDodajanje(false);
          pushToast('Soba je dodana.');
        }} disabled={!ime.trim()} data-id="element-131" data-mp="3tee9wo8tncv6">
              Dodaj
            </Button>
          </div>}
      </Card>

      <div className="grid gap-3 lg:grid-cols-2" data-id="element-132" data-mp="20ky5kmam3xfp">
        {nastanitev.sobe.map(r => {
        const gostje = udelezenci.filter(m => dodeljeno[m.id] === r.id);
        const prosto = r.kapaciteta - gostje.length;
        return <Card key={r.id} className="p-4" data-id="element-133" data-mp="1rlb2zx65cll2">
              <div className="flex items-start justify-between gap-3" data-id="element-134" data-mp="1mkx4p6uckq08">
                <div data-id="element-135" data-mp="2k7w43pcfcc1t">
                  <h3 className="text-sm font-semibold text-ink" data-id="element-136" data-mp="2k8e1ipatowbb">{r.ime}</h3>
                  <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-137" data-mp="2f8swpy8z1tqo">
                    Kapaciteta {r.kapaciteta} · zasedeno {gostje.length}
                  </p>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-138" data-mp="2afq35q472kah">
                  <Badge className={prosto === 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-800 border-amber-200'} data-id="element-139" data-mp="25i6oip3l45i0">
                    {prosto === 0 ? 'polna' : `${prosto} prostih`}
                  </Badge>
                  <button onClick={() => removeRoom(event.id, r.id)} aria-label={`Odstrani ${r.ime}`} className="rounded-md p-1.5 text-ink-muted transition-colors duration-150 ease-out hover:bg-rose-50 hover:text-rose-600" data-id="element-140" data-mp="10c84xyq82nw0">
                    <Trash2Icon className="h-4 w-4" data-id="element-141" data-mp="cluol5h02dh9" />
                  </button>
                </div>
              </div>

              <ul className="mt-3 space-y-1.5" data-id="element-142" data-mp="hm0981ouqzpu">
                {gostje.map(m => <li key={m.id} className="flex items-center gap-2 rounded-lg border border-line px-2.5 py-1.5" data-id="element-143" data-mp="3mhdk8d89guf">
                    <span className="min-w-0 flex-1 truncate text-[13px] text-ink" data-id="element-144" data-mp="1xzqgfhb9q43p">{fullName(m)}</span>
                    <span className="shrink-0 text-[12px] text-ink-muted" data-id="element-145" data-mp="22z3c8rk4alif">{voiceLabel[m.voice]}</span>
                    <button onClick={() => assignRoom(event.id, m.id, null)} aria-label={`Odstrani ${fullName(m)} iz sobe`} className="rounded p-0.5 text-ink-muted transition-colors duration-150 ease-out hover:text-rose-600" data-id="element-146" data-mp="2d1yutnjwe5ko">
                      <XIcon className="h-3.5 w-3.5" data-id="element-147" data-mp="2h3tyuzt7qx25" />
                    </button>
                  </li>)}
                {gostje.length === 0 && <li className="rounded-lg bg-canvas px-2.5 py-2 text-[12px] text-ink-muted" data-id="element-148" data-mp="2qtvjtiq9rzcu">
                    Soba je prazna.
                  </li>}
              </ul>

              {prosto > 0 && brezSobe.length > 0 && <select aria-label={`Dodeli člana v ${r.ime}`} value="" onChange={e => {
            if (e.target.value) assignRoom(event.id, e.target.value, r.id);
          }} className={`${inputClass} mt-3`} data-id="element-149" data-mp="30te2zwycvh3u">
                  <option value="" data-id="element-150" data-mp="1uyq5my5wm4sm">Dodeli člana …</option>
                  {brezSobe.map(m => <option key={m.id} value={m.id} data-id="element-151" data-mp="1zw8iv0qyf66f">
                      {fullName(m)} — {voiceLabel[m.voice]}
                    </option>)}
                </select>}
            </Card>;
      })}
      </div>

      <Card className="p-5" data-id="element-152" data-mp="1c0nkiugh8s03">
        <div className="flex items-center justify-between gap-3" data-id="element-153" data-mp="1c1fg45a2qpqd">
          <h3 className="text-base font-semibold text-ink" data-id="element-154" data-mp="1gyp5jkxgr1e7">Udeleženci brez sobe</h3>
          <Badge className={brezSobe.length === 0 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-800 border-amber-200'} data-id="element-155" data-mp="1l129c3sngp8c">
            {brezSobe.length} oseb
          </Badge>
        </div>
        {brezSobe.length === 0 ? <p className="mt-3 text-[13px] text-ink-muted" data-id="element-156" data-mp="31e0xf82nnoct">Vsi udeleženci imajo dodeljeno sobo.</p> : <ul className="mt-3 divide-y divide-line" data-id="element-157" data-mp="3bho28oa57tso">
            {brezSobe.map(m => <li key={m.id} className="flex flex-wrap items-center gap-3 py-2.5" data-id="element-158" data-mp="36l7brnfh13cf">
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink" data-id="element-159" data-mp="31se5uw89noyr">{fullName(m)}</span>
                <span className="text-[12px] text-ink-muted" data-id="element-160" data-mp="2xqj8g2tghvq9">{voiceLabel[m.voice]}</span>
                <select aria-label={`Dodeli sobo za ${fullName(m)}`} value="" onChange={e => {
            if (e.target.value) assignRoom(event.id, m.id, e.target.value);
          }} className={`${inputClass} w-auto`} data-id="element-161" data-mp="2ssrg57owg7lj">
                  <option value="" data-id="element-162" data-mp="2euaywmteuyp5">Izberi sobo …</option>
                  {nastanitev.sobe.filter(r => udelezenci.filter(x => dodeljeno[x.id] === r.id).length < r.kapaciteta).map(r => <option key={r.id} value={r.id} data-id="element-163" data-mp="3a8m7dkkyto40">
                        {r.ime}
                      </option>)}
                </select>
              </li>)}
          </ul>}
      </Card>
    </div>;
}